// src/pages/inventory/StockList.js
import React, { useState, useEffect, Fragment, useMemo } from "react";
import { Card, Col, Table, Input, CardBody, Label, Button } from "reactstrap";
import { FormLabel, OverlayTrigger, Tooltip, Form, Modal, Row } from "react-bootstrap";
import { Filter, PlusCircle, X } from "react-feather";
import Skeleton from "react-loading-skeleton";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { useLocation, useNavigate } from "react-router-dom";
import { getStockList, PayPurchasePayment } from "./StockService";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import Select from 'react-select';
import { suppliersMasterAPI } from "../PharmacySuppliers/SupplierService";
import AsyncSelect from "react-select/async";
import { useAtom } from "jotai";
import { StockListPageAtom, StockListPageSizeAtom } from "./StockAtoms";
import { FiRotateCcw } from "react-icons/fi";
import { FaPlusCircle } from "react-icons/fa";
import { useCurrency } from "../../_helper/CurrencyContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";

// Dummy static stock purchase history
// export const stockListData = [
// 	{
// 		id: 1,
// 		purchaseNo: "PUR-1001",
// 		date: "2025-08-15",
// 		supplier: "Cipla Ltd.",
// 		subtotal: 1200,
// 		transport: 50,
// 		discount: 100,
// 		netTotal: 1150,
// 		totalPaid: 1000,
// 		totalDue: 150,
// 		paymentStatus: "Partial",
// 		status: "Completed",
// 	},
// 	{
// 		id: 2,
// 		purchaseNo: "PUR-1002",
// 		date: "2025-08-16",
// 		supplier: "Sun Pharma",
// 		subtotal: 2000,
// 		transport: 100,
// 		discount: 200,
// 		netTotal: 1900,
// 		totalPaid: 1900,
// 		totalDue: 0,
// 		paymentStatus: "Paid",
// 		status: "Completed",
// 	},
// 	{
// 		id: 3,
// 		purchaseNo: "PUR-1003",
// 		date: "2025-08-17",
// 		supplier: "HealthCare Equip",
// 		subtotal: 750,
// 		transport: 30,
// 		discount: 0,
// 		netTotal: 780,
// 		totalPaid: 500,
// 		totalDue: 280,
// 		paymentStatus: "Partial",
// 		status: "Pending",
// 	},
// ];

const StockList = () => {
	const { getCurrencySymbol, currency, rates } = useCurrency();

	const [stockData, setStockData] = useState([]);
	const [page, setPage] = useAtom(StockListPageAtom);
	const [size, setSize] = useAtom(StockListPageSizeAtom);
	const [totalItems, setTotalItems] = useState(0);
	const [searchValue, setSearchValue] = useState("");
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');
	const [supplier, setSupplier] = useState([]);
	const [purchaseType, setPurchaseType] = useState("purchase");
	const [selectedSupplier, setSelectedSupplier] = useState('');
	const { setLoading } = useLoader();
	const [skeletonLoading, setSkeletonLoading] = useState(false);
	const [isClearing, setIsClearing] = useState(false);
	const [balanceSort, setBalanceSort] = useState(null);
	const { t } = useTranslation();
	const { pharmacyPOS } = usePharmacy();
	const history = useNavigate();

	// Tooltips
	// const [editTooltipOpen, setEditTooltipOpen] = useState(false);
	const [returnTooltipOpen, setReturnTooltipOpen] = useState(false);
	const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
	// const [viewItemsTooltipOpen, setViewItemsTooltipOpen] = useState(false);
	// const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
	// const toggleEditTooltip = () => setEditTooltipOpen(!editTooltipOpen);
	const toggleReturnTooltip = () => setReturnTooltipOpen(!returnTooltipOpen);
	const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
	const [settlementTooltipOpen, setSettlementTooltipOpen] = useState(false);
	// const toggleViewItemsTooltip = () => setViewItemsTooltipOpen(!viewItemsTooltipOpen);
	// const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);

	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	// Settlement Modal
	const [errors, setErrors] = useState({});
	const disableScrollProps = useDisableScrollOnFocus();
	const [showSettlementModal, setShowSettlementModal] = useState(false);
	const [settlementData, setSettlementData] = useState({});
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
	const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);
	const toggleSettlementTooltip = () =>
		setSettlementTooltipOpen(!settlementTooltipOpen);
	const location = useLocation();
	const passedSupplierData = location.state?.supplier || null;


	useEffect(() => {
		getStocks();
	}, [page, size, purchaseType, supplier]);

	const getStocks = async () => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			page: page,
			limit: size,
			search: searchValue.trim(),
			supplierId: supplier?._id,
			startDate: fromDate,
			endDate: toDate,
			purchaseType: purchaseType
		};
		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await getStockList(body);
			const purchases = response?.data?.data?.purchases;

			if (Array.isArray(purchases)) {
				setStockData(purchases);
				const totalCount = response.data.data.total || 0;
				setTotalItems(Math.ceil(totalCount / size));
			} else {
				// API returned failure / no data
				setStockData([]);
				setTotalItems(0);
			}
		} catch (error) {
			console.error("Error fetching sales history:", error);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	};

	const sortedStockData = useMemo(() => {
		if (!balanceSort) return stockData;

		return [...stockData].sort((a, b) => {
			const aBal = Number(a.balance || 0);
			const bBal = Number(b.balance || 0);

			return balanceSort === "desc"
				? bBal - aBal
				: aBal - bBal;
		});
	}, [stockData, balanceSort]);

	useEffect(() => {
		// Reset table when switching purchase / return
		setStockData([]);
		setTotalItems(0);
		setPage(1);
		setBalanceSort(null);
	}, [purchaseType]);

	// When navigated from ManageSupplier
	useEffect(() => {
		if (passedSupplierData) {
			setSupplier({ _id: passedSupplierData._id, });
			setSelectedSupplier({
				value: passedSupplierData._id,
				label: `${passedSupplierData.companyName} [${passedSupplierData.contactPerson}]`, // temporary until Future API loads
				raw: passedSupplierData
			});
		}
	}, [passedSupplierData]);

	const handleFromDateChange = (event) => {
		setFromDate(event.target.value);
	};

	const handleToDateChange = (event) => {
		setToDate(event.target.value);
	};

	const handleSearchButtonClick = () => {
		if (searchValue.trim !== "") {
			setPage(1); // Reset page to 1
			getStocks(); // Trigger the API call for search
		}
	};

	const handleClear = () => {
		setIsClearing(true);
		setSearchValue(''); // Clear the search value
		setSupplier(null);
		setSelectedSupplier(null); // Clear AsyncSelect value
		setSearchQuerySupplier('');
		setFromDate('');
		setToDate('');
		setPurchaseType('purchase');
	};

	useEffect(() => {
		if (isClearing) {
			getStocks();
			setIsClearing(false);
		}
	}, [isClearing]);

	const handlePageChange = (pageNumber) => setPage(pageNumber);

	const handlePageSizeChange = (e) => {
		setSize(parseFloat(e.target.value));
		setPage(1);
	};

	const handleEditClick = (id) => {
		history(`${process.env.PUBLIC_URL}/update-purchase/${id}`);
	};

	const handleReturnClick = (id) => {
		history(`${process.env.PUBLIC_URL}/return-purchase/${id}`);
	};

	const handleViewClick = (id) => {
		history(`${process.env.PUBLIC_URL}/view-purchase/${id}`);
	};

	const handleViewItemsClick = (id) => {
		history(`${process.env.PUBLIC_URL}/purchase-items/${id}`);
	};

	const handleDeleteClick = (id) => {
		const updated = stockData.filter((s) => s.id !== id);
		setStockData(updated);
		setTotalItems(updated.length);
	};

	const addForm = () => {
		history(`${process.env.PUBLIC_URL}/add-purchase`);
	}

	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, '0');
		const month = date.toLocaleString(undefined, { month: 'short' });
		const year = date.getFullYear();
		return `${day} ${month} ${year}`; // Example: May 07, 2025
	};

	const loadSuppliers = async (inputValue) => {
		if (!inputValue) return [];

		const body = {
			search: inputValue,
			clinicId: pharmacyPOS?.hospital_id,
		};

		try {
			setLoading(true);
			const response = await suppliersMasterAPI(body);
			const suppliers = response.data.data || [];

			return suppliers.map((m) => ({
				value: m._id,
				// label: `${m.companyName} [${m.countryCode} ${m.phone}]`,
				label: `${m.companyName} [${m.contactPerson}]`,
				raw: m // keep full object for later use
			}));
		} catch (error) {
			console.error("Error fetching medicines:", error);
			return [];
		} finally {
			setLoading(false);
		}
	};

	const [searchQuerySupplier, setSearchQuerySupplier] = useState('');

	const handleSupplierSearchChange = (newValue) => {
		setSearchQuerySupplier(newValue);
	};

	// ✅ When a medicine is selected
	const handleSelectSupplierOld = (selectedOption) => {
		if (!selectedOption) return;

		const supplier = selectedOption.raw;
		setSupplier(supplier);
	};

	const handleSelectSupplier = (selectedOption) => {
		if (!selectedOption) {
			setSupplier(null);
			setSelectedSupplier(null); // Only clear if user clears the field
			return;
		}
		const supplier = selectedOption.raw;
		setSupplier(supplier);
		setSelectedSupplier(selectedOption); // Show selected supplier in AsyncSelect
	};

	const formatAmount = (cur, amount) => {
		if (!cur) return Number(amount || 0).toFixed(2);
		const prefix = getCurrencySymbol(cur, "prefix");
		const suffix = getCurrencySymbol(cur, "suffix");
		return prefix + Number(amount || 0).toFixed(2) + suffix;
	};

	// Open Settlement Modal
	const openSettlementModal = (purchase) => {

		setSettlementData({
			purchaseId: purchase._id,
			supplierId: purchase.supplierId,
			supplierName: purchase.supplierName,

			totalAmount: purchase.NetTotal,
			totalPaid: purchase.totalPaid,
			balance: purchase.balance,
			rates,
			currency: purchase.currencyType || currency,
			amount: purchase.balance, // default to outstanding amount

			paymentType: "cash",
			isSettle: purchase.isSettled || false,
			baseCurrency: currency,
		});
		setErrors({});
		setShowSettlementModal(true);
	};

	const handleSettlementSave = async () => {

		let validation = {};
		const payingAmount = Number(settlementData.amount || 0);
		const balance = Number(settlementData.balance || 0);

		if (!settlementData.amount || settlementData.amount <= 0)
			validation.amount = "Amount must be greater than 0";

		if (!settlementData.paymentType)
			validation.paymentType = "Payment type required";

		setErrors(validation);
		if (Object.keys(validation).length > 0) return;

		// ---------- LOGIC ----------
		let isPaid = false;
		let isPartialPaid = false;

		if (payingAmount < balance) {
			isPaid = false;
			isPartialPaid = true;
		} else if (payingAmount === balance) {
			isPaid = true;
			isPartialPaid = false;
		}

		const body = {
			payingAmount: settlementData.amount,
			purchaseType: 'sPayment',
			currencyType: settlementData.currency,
			purchaseId: settlementData.purchaseId,
			isPaid,
			isPartialPaid,
			isSettled: settlementData.isSettle
			// clinicId: pharmacyPOS?.hospital_id,
			// supplierId: settlementData.supplierId,
			// pharmacyId: pharmacyPOS?._id,
			// paymentType: settlementData.paymentType,
		};

		try {
			setLoading(true);
			const response = await PayPurchasePayment(body);

			if (response.data.status === "success") {
				toast.success("Payment updated successfully");
				setShowSettlementModal(false);
				getStocks(); // refresh list
			}

		} catch (error) {
			console.error("Payment Error:", error);
			toast.warning("Something went wrong");
		} finally {
			setLoading(false);
		}
	};

	const handleSettlePayment = () => {
		const confirmText = settlementData.isSettle ? "Unsettle Payment?" : "Settle Payment?";

		Swal.fire({
			title: "Are you sure?",
			text: confirmText,
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes",
			cancelButtonText: "Cancel",
		}).then((result) => {
			if (result.isConfirmed) {
				setSettlementData({
					...settlementData,
					isSettle: !settlementData.isSettle,
				});
			}
		});
	};

	const handlePurchaseTypeChange = (type) => {
		setPurchaseType(type);
		setSearchValue('');
		setSupplier(null);
		setSelectedSupplier(null);
		setFromDate('');
		setToDate('');
	};

	const isDark = document.body.classList.contains("dark-only");
	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Purchase History")}
				parent={t("Dashboard")}
				title={t("Purchase History")}
			/>
			<Col sm="12">
				<Card>
					<CardBody>
						<div className="d-flex justify-content-end align-items-center">
							<button
								className="btn btn-primary d-flex align-items-center"
								// style={{ color: 'white', height: "40px" }}
								to=""
								onClick={addForm}>
								<FaPlusCircle className="me-2" /> {t("Purchase Items")}
							</button>
						</div>
						{/* Search & Filter */}
						<div className="row">

							<Col sm="12" lg="4" xl="4" md="4" className="mb-3">
								<label className="form-label">{t('Supplier')}</label>
								<AsyncSelect
									cacheOptions
									loadOptions={loadSuppliers}
									defaultOptions={[]} // nothing until search
									value={selectedSupplier}
									onChange={handleSelectSupplier}
									placeholder={t("Search & select a supplier")}
									onInputChange={handleSupplierSearchChange}
									noOptionsMessage={() =>
										searchQuerySupplier
											? t("We couldn't find any supplier matching your search. Please try a different name.")
											: t('Start typing to search...')
									}
									isClearable={true}
									theme={(theme) => ({
										...theme,
										colors: {
											...theme.colors,
											primary25: isDark ? "#373737" : "#e6f7ffff",

										},
									})}
								/>
							</Col>
							<Col sm="12" lg="4" xl="4" md="4" className="mb-3">
								<label className="form-label">{t("From Date")}</label>
								<input
									type="date"
									style={{ lineHeight: 2, fontSize: "1rem !important" }}
									className="form-control "
									value={fromDate}
									onChange={handleFromDateChange}
								/>
							</Col>
							<Col sm="12" lg="4" xl="4" md="4" className="mb-3">
								<label className="form-label">{t("To Date")}</label>
								<input
									type="date"
									style={{ lineHeight: 2, fontSize: "1rem !important" }}
									className="form-control "
									value={toDate}
									onChange={handleToDateChange}
								/>
							</Col>
							<Col sm="12" lg="6" xl="6" md="6">
								<label className="form-label">{t("Search")}</label>
								<Input
									name="SearchData"
									id="SearchData"
									type="text"
									placeholder={t("Search by Purchase No")}
									value={searchValue}
									onChange={(e) => setSearchValue(e.target.value)}
									style={{
										lineHeight: 1.8, fontSize: "1rem !important"
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											handleSearchButtonClick();
										}
									}}
								/>
							</Col>
							<Col sm="12" lg="4" xl="4" md="4">
								<label className="form-label">{t("Purchase Type")}</label>
								<div className="mt-2 d-flex align-items-center" style={{ gap: "20px" }}>
									<div className="radio radio-primary">
										<Input
											id="purchase-type-purchase"
											type="radio"
											name="purchaseType"
											value="purchase"
											checked={purchaseType === "purchase"}
											onChange={() => handlePurchaseTypeChange("purchase")}
										/>
										<Label for="purchase-type-purchase" className='ps-3'>
											{t("Purchase")}
										</Label>
									</div>
									<div className="radio radio-primary">
										<Input
											id="purchase-type-return"
											type="radio"
											name="purchaseType"
											value="return"
											checked={purchaseType === "return"}
											onChange={() => handlePurchaseTypeChange("return")}
										/>
										<Label for="purchase-type-return" className='ps-3'>
											{t("Return")}
										</Label>
									</div>
								</div>
							</Col>
							<Col className="col-xl-2 col-lg-4 col-md-4 col-sm-4 d-flex justify-content-start align-items-center" style={{ gap: '18px', marginTop: "20px" }}>
								<button id='FilterToolTip' className='btn btn-primary' style={{ height: "40px" }} onClick={handleSearchButtonClick}>
									<Filter />
								</button>
								<ToolTip
									attrToolTip={{
										placement: 'top',
										isOpen: filterTooltipOpen,
										target: 'FilterToolTip',
										toggle: toggleFilterTooltip,
									}} >
									<strong>{t("Filter")}</strong>
								</ToolTip>
								<button id='ClearToolTip' className='btn btn-secondary' style={{ height: "40px" }} onClick={handleClear}>
									<X />
								</button>
								<ToolTip
									attrToolTip={{
										placement: 'top',
										isOpen: clearTooltipOpen,
										target: 'ClearToolTip',
										toggle: toggleClearTooltip,
									}} >
									<strong>{t("Clear")}</strong>
								</ToolTip>
							</Col>
						</div>
					</CardBody>
				</Card>

				<Card>
					{/* Table */}
					<div className="card-block">
						<div className="row" style={{ padding: "12px 24px" }}>
							<Col sm="12">
								<div className="rounded table-responsive">
									<Table className="mb-0 table-center table-bordered table-hover">
										<thead>
											<tr>
												<th>#</th>
												<th>{t("Purchase No")}</th>
												<th>{t("Purchase Date")}</th>
												<th>{t("Supplier")}</th>
												<th>{t("Total Items")}</th>
												<th>{t("Grand Total")}</th>
												<th>{t("Total Paid")}</th>
												<th
													role="button"
													className="user-select-none"
													onClick={() =>
														setBalanceSort(prev =>
															prev === null ? "desc" : prev === "desc" ? "asc" : null
														)
													}
												>
													{t("Outstanding Balance")}
													<span className="ms-1">
														{balanceSort === null && "⇅"}
														{balanceSort === "desc" && "▼"}
														{balanceSort === "asc" && "▲"}
													</span>
												</th>
												<th className="text-center">{t("Action")}</th>
											</tr>
										</thead>
										<tbody>
											{skeletonLoading ? (
												[...Array(5)].map((_, i) => (
													<tr key={i}>
														{Array(8)
															.fill()
															.map((_, idx) => (
																<td key={idx}>
																	<Skeleton height={20} />
																</td>
															))}
													</tr>
												))
											) : sortedStockData.length > 0 ? (
												sortedStockData.map((stock, index) => (
													<tr key={index} style={{ backgroundColor: stock.isRetruned ? '#f8d7da' : 'inherit' }}>
														<td onClick={() => console.log(stock)}>{(page - 1) * size + index + 1}</td>
														<td>{stock.purchaseNumber}
															{stock.isReturned && <span className="badge bg-danger ms-2">{t("Returned")}</span>}
														</td>
														<td>{formatDate(stock.purchaseDate)}</td>
														<td>{stock.supplierName}</td>
														<td>{stock.totalItem}</td>
														<td>
															{getCurrencySymbol()}
															{stock.NetTotal}</td>
														<td>
															{!stock.isRetruned
																? `${getCurrencySymbol()}${stock.totalPaid}`
																: "-"}
														</td>
														{/* <td>{!stock.isRetruned ? `${stock.currencyType === "USD" && "$"}${stock.totalPaid} ${stock.currencyType === "ZiG" && "ZiG"}` : '-'}</td> */}
														<td>
															{stock.balance ? (
																<strong className="text-danger">{getCurrencySymbol()}{stock.balance}</strong>
															) : ('-')
															}
														</td>
														<td className="text-center">
															{Number(stock.balance) > 0 && (
																<button
																	className="success-action-button me-1"
																	id={`SettlementTooltip`}
																	onClick={() => openSettlementModal(stock)}
																>
																	<i
																		className="fa fa-money"
																		style={{ fontSize: "18px" }}
																	></i>
																	<ToolTip
																		attrToolTip={{
																			placement: "top",
																			isOpen: settlementTooltipOpen,
																			target: `SettlementTooltip`,
																			toggle: toggleSettlementTooltip,
																		}}
																	>
																		<strong>{t("Pay")}</strong>
																	</ToolTip>
																</button>
															)}
															<button
																className="success-action-button me-1"
																id="ViewToolTip"
																onClick={() => handleViewClick(stock?._id)}
															>
																<i
																	className="fa fa-eye"
																	style={{ fontSize: "18px" }}
																></i>
																<ToolTip
																	attrToolTip={{
																		placement: "top",
																		isOpen: viewTooltipOpen,
																		target: "ViewToolTip",
																		toggle: toggleViewTooltip,
																	}}
																>
																	<strong>{t("View")}</strong>
																</ToolTip>
															</button>
															{/* <button
																className="success-action-button me-1"
																id="EditToolTip"
																onClick={() => handleEditClick(stock?._id)}
															>
																<i
																	className="fa fa-pencil-square"
																	style={{ fontSize: "18px" }}
																></i>
																<ToolTip
																	attrToolTip={{
																		placement: "top",
																		isOpen: editTooltipOpen,
																		target: "EditToolTip",
																		toggle: toggleEditTooltip,
																	}}
																>
																	<strong>{t("Edit")}</strong>
																</ToolTip>
															</button> */}
															<button
																className="success-action-button me-1"
																id="ReturnToolTip"
																onClick={() => handleReturnClick(stock?._id)}
															>
																<i className="fa fa-undo" style={{ fontSize: "18px" }}></i>
																<ToolTip
																	attrToolTip={{
																		placement: "top",
																		isOpen: returnTooltipOpen,
																		target: "ReturnToolTip",
																		toggle: toggleReturnTooltip,
																	}}
																>
																	<strong>{t("Return")}</strong>
																</ToolTip>
															</button>

															{/* <button
																className="success-action-button me-1"
																id="ViewItemToolTip"
																onClick={() => handleViewItemsClick(stock?._id)}
															>
																<i
																	className="fa fa-list"
																	style={{ fontSize: "18px" }}
																></i>
																<ToolTip
																	attrToolTip={{
																		placement: "top",
																		isOpen: viewItemsTooltipOpen,
																		target: "ViewItemToolTip",
																		toggle: toggleViewItemsTooltip,
																	}}
																>
																	<strong>{t("View Items")}</strong>
																</ToolTip>
															</button> */}
															{/* <button
																className="danger-action-button"
																id="DeleteToolTip"
																onClick={() => handleDeleteClick(stock?.id)}
															>
																<i
																	className="fa fa-trash"
																	style={{ fontSize: "18px" }}
																></i>
																<ToolTip
																	attrToolTip={{
																		placement: "top",
																		isOpen: deleteTooltipOpen,
																		target: "DeleteToolTip",
																		toggle: toggleDeleteTooltip,
																	}}
																>
																	<strong>{t("Delete")}</strong>
																</ToolTip>
															</button> */}
														</td>
													</tr>
												))
											) : (
												<tr className="text-center">
													<td colSpan="13">{t("No purchase records available.")}</td>
												</tr>
											)}
										</tbody>
									</Table>
								</div>
							</Col>

							{/* Pagination */}
							<Col sm="12" className="mt-4">
								<div className="text-center d-md-flex align-items-center justify-content-between">
									<div className="pt-3 show-entries">
										<FormLabel>{t("Show")}&nbsp;</FormLabel>
										<select
											value={size}
											onChange={handlePageSizeChange}
											className="form-control"
											style={{ display: "unset", width: "auto" }}
										>
											<option value={5}>5</option>
											<option value={10}>10</option>
											<option value={20}>20</option>
											<option value={30}>30</option>
											<option value={50}>50</option>
										</select>
										&nbsp;
										<FormLabel>{t("entries")}</FormLabel>
									</div>
									<MyPagination
										totalPages={totalItems}
										currentPage={page}
										onPageChange={handlePageChange}
									/>
								</div>
							</Col>
						</div>
					</div>
				</Card>
			</Col>

			{/* Settlement Modal */}
			<Modal
				show={showSettlementModal}
				onHide={() => setShowSettlementModal(false)}
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>{t("Add Payment")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form>
						<Form.Group className="mb-3">
							<Form.Label>{t("Payment Type")}</Form.Label>
							<Form.Select
								value={settlementData.paymentType}
								onChange={(e) =>
									setSettlementData({
										...settlementData,
										paymentType: e.target.value,
									})
								}
								isInvalid={!!errors.paymentType}
							>
								<option value="cash">{t("Cash")}</option>
								<option value="card">{t("Card")}</option>
								<option value="upi">{t("UPI")}</option>
							</Form.Select>
							<Form.Control.Feedback type="invalid">
								{errors.paymentType}
							</Form.Control.Feedback>
						</Form.Group>
						{/* <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
							<strong>{t("Outstanding Balances")}:</strong>

							{Object.entries(settlementData.rates).map(([cur]) => (
								<div key={cur} className="d-flex justify-content-between mt-1">
									<span>{cur}</span>
									<span className="fw-bold">
										{formatAmount(cur, settlementData.remainingAmounts?.[cur] || settlementData.balances?.[cur])}
									</span>
								</div>
							))}
						</div> */}
						<Form.Group className="mb-3">
							<Form.Label>{t("Amount")}</Form.Label>
							<Form.Control
								type="number"
								value={settlementData.amount}
								onChange={(e) => {
									let val = Number(e.target.value || 0);
									const balance = Number(settlementData.balance || 0);

									// Auto-fix overpay
									if (val > balance) {
										val = balance;
									}

									setSettlementData({
										...settlementData,
										amount: val,
									});
								}}
								{...disableScrollProps}
								isInvalid={!!errors.amount}
							/>
							<Form.Control.Feedback type="invalid">
								{errors.amount}
							</Form.Control.Feedback>
						</Form.Group>
						{/* <CurrencyAmountInput
							label={t("Amount")}
							amount={settlementData.amount}
							onAmountChange={(value) => setSettlementData(prev => ({
								...prev,
								amount: value
							}))
							}
							onCurrencyChange={(cur) => setSettlementData(prev => ({
								...prev,
								currency: cur
							}))
							}
							disabled={false}
							errors={errors}
							disableScrollProps={disableScrollProps}
							defaultCurrency={currency}
							originalAmount={settlementData.remainingPrimary}
							paidInCurrency={settlementData.currency}
							settlementData={settlementData}
						/> */}
						<div className="p-2 my-3 d-flex justify-content-end flex-column payment-info bg-primary-subtle">
							<div className="mb-1">
								<span className="ms-3 text-danger">
									{t("Remaining")}: {getCurrencySymbol(settlementData.currency)}
									{(() => {
										const cur = settlementData.currency;

										const totalBalance = settlementData.balance || 0;
										const paidAmount = Number(settlementData.amount || 0);

										const remaining = Math.max(0, totalBalance - paidAmount);

										return remaining.toFixed(2);
									})()}
								</span>
							</div>
						</div>
						<Form.Group className="mb-3">
							<Form.Label>{t("Settlement")}</Form.Label>
							<Row>
								<Col sm="6" className="mb-2">
									<div
										className="checkbox checkbox-solid-light"
										style={{ display: "flex", alignItems: "center" }}
									>
										<Input
											id={`isSettle-checkbox`}
											type="checkbox"
											checked={settlementData.isSettle}
											className="me-2"
											onChange={handleSettlePayment}
										/>
										<label
											htmlFor={`isSettle-checkbox`}
											style={{ fontSize: "14px", margin: 0 }}
										>
											<strong>{t("Payment Settlement")}</strong>
										</label>
									</div>
								</Col>
							</Row>
						</Form.Group>
					</Form>
				</Modal.Body>
				<Modal.Footer>
					<Button color="primary" onClick={handleSettlementSave}>
						{t("Save")}
					</Button>
					<Button
						color="secondary"
						onClick={() => setShowSettlementModal(false)}
					>
						{t("Cancel")}
					</Button>
				</Modal.Footer>
			</Modal>
		</Fragment>
	);
};

export default StockList;
